import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader } from 'lucide-react';
import DocumentViewer from '../../components/DocumentViewer';
import { hasPageLocation } from '../../components/documentViewerGeometry';
import type { DocumentSource } from '../../components/DocumentViewer';
import FieldCard from '../../components/FieldCard';
import { EmptyState } from '../../components/ui/EmptyState';
import { JobType } from '../../graphql/types';
import type { Document, File as FileType, UploadCredentials } from '../../graphql/types';
import { Rail } from '../Rail';
import { TOP_BAR_HEIGHT_PX } from '../TopBar';
import { readWorkspaceParams, writeWorkspaceParams } from '../urlState';
import { buildBackToAnalyzeParams, cameFromAnalyze } from '../analyze/analyzeNavigation';
import { recordFieldChange } from '../analyze/analyzeStale';
import { loadFilePdf, saveFileReview } from './extractData';
import {
  countConfirmed,
  fieldNote,
  findFieldByParam,
  flattenGroups,
  groupExtractFields,
  isConfirmed,
  loadConfirmations,
  loadEdits,
  parsePageRange,
  saveConfirmations,
  saveEdits as persistEdits,
  setConfirmation,
} from './extractModel';
import type { ConfirmationOverrides, ExtractField, FieldEdits } from './extractModel';

/**
 * ExtractMode (DD and BA), built from board `1a`.
 *
 * Layout: Rail (272px) | Document_Viewer (flexible) | field list (400px).
 * Header of the field list: "n of m confirmed". Footer: "Continue to Analyze".
 *
 * Data and save calls come from the legacy `FileReview` page: the file's PDF
 * from S3, the documents' `extractedFields`, and `finalizeDocumentReview`.
 *
 * - Selecting a Field_Card highlights its source on the page; selecting a
 *   highlighted value on the page selects its Field_Card (Requirement 7.2).
 * - Confirmation persists per loan (Requirement 7.4).
 * - A low-confidence or conflict note shows until the field is confirmed (7.5).
 * - Selection (`doc`, `field`) lives in the Workspace URL params (task 1.3).
 *
 * The Rail belongs to the Workspace (`WorkflowRail`, the same in every Mode),
 * so this body has none. The `rail` prop is only for tests and previews that
 * render the Mode on its own; it adds a Rail beside the viewer.
 *
 * Requirements: 7.1, 7.2, 7.4, 7.6.
 */
export interface ExtractModeProps {
  /** The file shown in the Document_Viewer. */
  file: FileType;
  /** Documents detected in the file, with their `extractedFields`. */
  documents: ReadonlyArray<Document>;
  uploadBucket?: string;
  uploadCredentials?: UploadCredentials | null;
  /** For tests and previews only: a Rail rendered inside the body. The Workspace owns the real one. */
  rail?: React.ReactNode;
  /** Accessible name of the Rail. */
  railLabel?: string;
  /** Feature the URL params are read for. */
  feature?: JobType.DD | JobType.BA;
  /** Called after the review is saved. Defaults to opening Analyze mode. */
  onContinue?: () => void;
  /** Shown above the viewer caption, for example a file switcher. */
  viewerHeader?: React.ReactNode;
  /** Called after a field is confirmed or unconfirmed, once it is saved. */
  onConfirmationsChange?: () => void;
}

const ACTION_BUTTON =
  'inline-flex min-w-[190px] items-center justify-center gap-2 rounded-lg bg-[#834DFB] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#6E2EF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#834DFB]';

export const ExtractMode: React.FC<ExtractModeProps> = ({
  file,
  documents,
  uploadBucket,
  uploadCredentials,
  rail,
  railLabel = 'Documents',
  feature = JobType.DD,
  onContinue,
  viewerHeader,
  onConfirmationsChange,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Fields ────────────────────────────────────────────────────────────────
  const groups = useMemo(() => groupExtractFields(documents), [documents]);
  const fields = useMemo(() => flattenGroups(groups), [groups]);

  // ── Confirmation, kept per loan ───────────────────────────────────────────
  const [overrides, setOverrides] = useState<ConfirmationOverrides>(() =>
    loadConfirmations(file.jobId, file.loanId)
  );
  // ── Inline edits, kept per loan until the file is saved ───────────────────
  const [edits, setEdits] = useState<FieldEdits>(() => loadEdits(file.jobId, file.loanId));
  useEffect(() => {
    setOverrides(loadConfirmations(file.jobId, file.loanId));
    setEdits(loadEdits(file.jobId, file.loanId));
  }, [file.jobId, file.loanId]);

  const toggleConfirmed = (field: ExtractField) => {
    const next = setConfirmation(overrides, field, !isConfirmed(field, overrides));
    setOverrides(next);
    saveConfirmations(file.jobId, file.loanId, next);
    // Findings that come from this field become out of date in Analyze (9.5).
    recordFieldChange(file.jobId, file.loanId, field.id);
    onConfirmationsChange?.();
  };

  const editField = (field: ExtractField, value: string) => {
    const nextEdits: FieldEdits = { ...edits, [field.id]: value };
    setEdits(nextEdits);
    persistEdits(file.jobId, file.loanId, nextEdits);
    // An edit is as good as a confirmation, so the field no longer waits.
    const nextOverrides = setConfirmation(overrides, field, true);
    setOverrides(nextOverrides);
    saveConfirmations(file.jobId, file.loanId, nextOverrides);
    recordFieldChange(file.jobId, file.loanId, field.id);
    onConfirmationsChange?.();
  };

  const valueOf = (field: ExtractField) =>
    Object.prototype.hasOwnProperty.call(edits, field.id) ? edits[field.id] : field.value;
  const confidenceOf = (field: ExtractField) =>
    Object.prototype.hasOwnProperty.call(edits, field.id) ? 1 : field.confidence;
  const extractedValueOf = (field: ExtractField) => {
    const edited = Object.prototype.hasOwnProperty.call(edits, field.id);
    if (edited) return field.extractedValue ?? field.value;
    return field.extractedValue;
  };

  // File-wide counts drive the footer hint; the header counts the selected document.
  const fileTotal = fields.length;
  const fileConfirmed = countConfirmed(fields, overrides);
  const remaining = fileTotal - fileConfirmed;

  // ── Selection, kept in the URL ────────────────────────────────────────────
  const params = readWorkspaceParams(searchParams, feature);
  const selected = findFieldByParam(fields, params.field);
  const selectedId = selected?.id ?? null;

  // The field list is scoped to one document: `doc`, else the selected field's
  // document, else the first document that has fields.
  const activeGroup =
    groups.find((g) => g.documentId === params.doc) ??
    groups.find((g) => g.documentId === selected?.documentId) ??
    groups.find((g) => g.fields.length > 0) ??
    groups[0];
  const activeFields = activeGroup?.fields ?? [];
  const activeDocId = activeGroup?.documentId ?? null;
  const total = activeFields.length;
  const confirmed = countConfirmed(activeFields, overrides);
  const percent = total === 0 ? 0 : Math.round((confirmed / total) * 100);

  const [page, setPage] = useState(1);

  // Page n of m inside the selected document (the prototype's page header).
  const docPages = parsePageRange(activeGroup?.pageRange);
  const pageCaption =
    docPages && page >= docPages.start && page <= docPages.end
      ? `Page ${page - docPages.start + 1} of ${docPages.end - docPages.start + 1}`
      : `Page ${page}`;
  const viewerCaption = activeGroup ? `${activeGroup.documentLabel} · ${pageCaption}` : pageCaption;

  const selectField = useCallback(
    (field: ExtractField) => {
      // An empty field has no page of its own: stay where the reviewer is.
      if (hasPageLocation(field)) setPage(field.page);
      setSearchParams(writeWorkspaceParams(searchParams, { field: field.id, doc: field.documentId }));
    },
    [searchParams, setSearchParams]
  );

  const selectById = (id: string) => {
    const field = fields.find((f) => f.id === id);
    if (field) selectField(field);
  };

  // Choosing another document opens its first page, unless the selected field
  // already sits in it (the next effect then moves to that field's page).
  useEffect(() => {
    if (docPages && selected?.documentId !== activeDocId) setPage(docPages.start);
    // Only react to the active document changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocId]);

  // A deep link or an outside change of `field` moves the viewer to its page.
  useEffect(() => {
    if (selected && hasPageLocation(selected)) setPage(selected.page);
    // Only react to the selected field changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    if (selectedId) cardRefs.current[selectedId]?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedId]);

  // Badges carry the number a field has in the list, so only the fields in the
  // list (the active document) get one.
  const highlights = useMemo(() => {
    const numbers = new Map(activeFields.map((f, i) => [f.id, String(i + 1)]));
    return fields.flatMap((f) =>
      f.boundingBox
        ? [{ id: f.id, page: f.page, boundingBox: f.boundingBox, badge: numbers.get(f.id) }]
        : []
    );
  }, [fields, activeFields]);

  // ── Document ──────────────────────────────────────────────────────────────
  const [pdfBytes, setPdfBytes] = useState<ArrayBuffer | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPdfBytes(null);
    setPdfError(null);
    setPdfLoading(true);
    loadFilePdf(file, uploadBucket, uploadCredentials)
      .then((bytes) => {
        if (!cancelled) setPdfBytes(bytes);
      })
      .catch((err) => {
        console.error('[ExtractMode] Failed to load the file:', err);
        if (!cancelled) {
          setPdfError('The file could not be loaded. Reload the page, or upload it again if this keeps happening.');
        }
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // The credentials object is only a fallback; the file decides the document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.fileId, file.s3Key, uploadBucket]);

  const source = useMemo<DocumentSource | null>(
    () => (pdfBytes ? { data: pdfBytes } : null),
    [pdfBytes]
  );

  // ── Continue to Analyze ───────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Continue is always available (board 1a); unconfirmed fields stay open for
  // later, and the hint beside the button says how many are left.
  const handleContinue = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const hasEdits = Object.keys(edits).length > 0;
      if (fileConfirmed > 0 || hasEdits) await saveFileReview(file, groups, edits);
      if (hasEdits) {
        // Edits are persisted on the documents now; drop the local buffer.
        setEdits({});
        persistEdits(file.jobId, file.loanId, {});
      }
      if (onContinue) onContinue();
      // Job-wide Analyze: keeps `loan`, clears the document/field selection.
      else {
        const next = buildBackToAnalyzeParams(searchParams);
        setSearchParams(writeWorkspaceParams(next, { doc: null, field: null, exception: null }));
      }
    } catch (err) {
      console.error('[ExtractMode] Failed to save the review:', err);
      setSaveError('The review could not be saved. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  // `-m-6` cancels the Workspace body padding; the height is the space under the
  // Top_Bar. The Rail is the Workspace's, so this row holds only the viewer and
  // the field list.
  return (
    <div
      data-testid="extract-mode"
      className="-m-6 flex min-w-0 bg-[#f7f6fb]"
      style={{ height: `calc(100vh - ${TOP_BAR_HEIGHT_PX}px)` }}
    >
      {rail ? (
        <Rail label={railLabel} className="h-full">
          {rail}
        </Rail>
      ) : null}

      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        {viewerHeader}
        <p
          data-testid="viewer-caption"
          className="shrink-0 truncate border-b border-gray-200 bg-[#f7f6fb] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500"
        >
          {viewerCaption}
        </p>
        <DocumentViewer
          source={source}
          loading={pdfLoading}
          error={pdfError}
          page={page}
          onPageChange={setPage}
          highlights={highlights}
          selectedId={selectedId}
          onSelect={selectById}
        />
      </div>

      <section
        aria-label="Extracted fields"
        className="flex h-full w-[400px] shrink-0 flex-col border-l border-gray-200 bg-white"
      >
        <header className="shrink-0 border-b border-gray-200 px-4 py-3">
          {cameFromAnalyze(searchParams) && (
            <button
              type="button"
              data-testid="back-to-analyze"
              onClick={() => setSearchParams(buildBackToAnalyzeParams(searchParams))}
              className="mb-2 inline-flex items-center gap-1 rounded text-[13px] font-bold text-[#5A21C9] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
            >
              <ArrowLeft size={14} strokeWidth={2} aria-hidden="true" />
              Back to Analyze
            </button>
          )}
          <div className="flex items-baseline justify-between gap-2">
            <h2
              className="truncate text-[15px] font-bold text-gray-900"
              title={`Extracted fields · ${activeGroup?.documentLabel ?? file.filename}`}
            >
              Extracted fields{activeGroup ? ` · ${activeGroup.documentLabel}` : ''}
            </h2>
            <span
              data-testid="confirmed-count"
              aria-live="polite"
              className="shrink-0 text-sm font-bold text-gray-700"
            >
              {confirmed} of {total} confirmed
            </span>
          </div>
          <div
            role="progressbar"
            aria-label="Fields confirmed"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={confirmed}
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100"
          >
            <div className="h-full bg-[#834DFB]" style={{ width: `${percent}%` }} />
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {total === 0 ? (
            <div className="p-4">
              <EmptyState
                title="No extracted fields"
                description="No fields were found in this file. Check that extraction finished, then reload."
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2.5 p-3">
              {activeFields.map((f, index) => (
                <div
                  key={f.id}
                  ref={(el) => {
                    cardRefs.current[f.id] = el;
                  }}
                >
                  <FieldCard
                    variant="confirm"
                    number={index + 1}
                    label={f.label}
                    value={valueOf(f)}
                    confidence={confidenceOf(f)}
                    page={f.page}
                    source={f.documentLabel}
                    note={fieldNote({ ...f, confidence: confidenceOf(f) })}
                    extractedValue={extractedValueOf(f)}
                    selected={f.id === selectedId}
                    onSelect={() => selectField(f)}
                    confirmed={isConfirmed(f, overrides)}
                    onConfirm={() => toggleConfirmed(f)}
                    onEdit={(value) => editField(f, value)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="shrink-0 border-t border-gray-200 px-4 py-3">
          {saveError && (
            <p role="alert" className="mb-2 text-xs text-[#991b1b]">
              {saveError}
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 text-xs text-gray-500">
              {remaining > 0
                ? `${remaining} ${remaining === 1 ? 'field' : 'fields'} left to confirm`
                : fileTotal > 0
                  ? 'All fields confirmed'
                  : ''}
            </p>
            <button
              type="button"
              onClick={() => void handleContinue()}
              disabled={saving}
              className={ACTION_BUTTON}
            >
              {saving ? (
                <>
                  <Loader size={14} strokeWidth={2} className="animate-spin" aria-hidden="true" />
                  Saving
                </>
              ) : (
                <>
                  Continue to Analyze
                  <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default ExtractMode;
